'use client';
import React from 'react';
import Link from 'next/link';
import { MapPin, Mail, Phone } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


function FacebookIcon() {
  return <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>;
}
function TwitterIcon() {
  return <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"/></svg>;
}
function InstagramIcon() {
  return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>;
}
function LinkedinIcon() {
  return <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6zM2 9h4v12H2z"/><circle cx="4" cy="4" r="2"/></svg>;
}
function YoutubeIcon() {
  return <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 0 0-1.95 1.96A29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58A2.78 2.78 0 0 0 3.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.95A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z"/><polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" fill="white"/></svg>;
}

const socialLinks = [
  { id: 'soc-fb', Icon: FacebookIcon, href: '#', label: 'Facebook' },
  { id: 'soc-tw', Icon: TwitterIcon, href: '#', label: 'Twitter' },
  { id: 'soc-ig', Icon: InstagramIcon, href: '#', label: 'Instagram' },
  { id: 'soc-li', Icon: LinkedinIcon, href: '#', label: 'LinkedIn' },
  { id: 'soc-yt', Icon: YoutubeIcon, href: '#', label: 'YouTube' },
];

const footerLinks = {
  discover: [
    { id: 'fl-blr', label: 'Bengaluru', href: '/listings-page?city=bengaluru' },
    { id: 'fl-mys', label: 'Mysuru', href: '/listings-page?city=mysuru' },
    { id: 'fl-hub', label: 'Hubli-Dharwad', href: '/listings-page?city=hubli' },
    { id: 'fl-mng', label: 'Mangaluru', href: '/listings-page?city=mangaluru' },
    { id: 'fl-bel', label: 'Belagavi', href: '/listings-page?city=belagavi' },
  ],
  categories: [
    { id: 'll-apt', label: 'Apartments', href: '/listings-page?type=apartment' },
    { id: 'll-vil', label: 'Villas', href: '/listings-page?type=villa' },
    { id: 'll-plt', label: 'Plots & Land', href: '/listings-page?type=plot' },
    { id: 'll-com', label: 'Commercial', href: '/listings-page?type=commercial' },
    { id: 'll-pg', label: 'PG / Hostel', href: '/listings-page?type=pg' },
  ],
  company: [
    { id: 'cp-about', label: 'About NammaPrice', href: '#' },
    { id: 'cp-careers', label: 'Careers', href: '#' },
    { id: 'cp-blog', label: 'Blog', href: '#' },
    { id: 'cp-press', label: 'Press', href: '#' },
    { id: 'cp-contact', label: 'Contact Us', href: '#' },
  ],
};

export default function Footer() {
  return (
    <footer className="bg-[#0F172A] text-white pb-20 lg:pb-0">
      {/* Main footer */}
      <div className="max-w-screen-xl mx-auto px-4 lg:px-8 pt-12 lg:pt-16 pb-8 lg:pb-10">
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-10">
          {/* Brand column */}
          <div className="lg:col-span-2">
            <Link href="/" className="flex items-center gap-2.5 mb-5">
              <div className="w-9 h-9 rounded-[11px] bg-[#D93025] flex items-center justify-center shadow-red">
                <MapPin size={16} className="text-white" strokeWidth={2.5} />
              </div>
              <span className="font-700 text-white text-[18px] tracking-tight">
                Namma<span className="text-[#FF6B6B]">Price</span>
              </span>
            </Link>
            <p className="text-white/45 text-[14px] leading-relaxed mb-6" style={{ maxWidth: '280px' }}>
              Karnataka&apos;s most trusted property marketplace. Buy, sell, or rent with confidence.
            </p>

            {/* Contact */}
            <div className="space-y-2.5 mb-6">
              <a href="tel:+918012345678" className="flex items-center gap-2.5 text-white/50 text-[13px] hover:text-white transition-colors">
                <div className="w-7 h-7 rounded-[8px] bg-white/8 flex items-center justify-center flex-shrink-0">
                  <Phone size={12} className="text-[#D93025]" />
                </div>
                +91 80 1234 5678
              </a>
              <a href="mailto:hello@nammaprice.in" className="flex items-center gap-2.5 text-white/50 text-[13px] hover:text-white transition-colors">
                <div className="w-7 h-7 rounded-[8px] bg-white/8 flex items-center justify-center flex-shrink-0">
                  <Mail size={12} className="text-[#D93025]" />
                </div>
                hello@nammaprice.in
              </a>
            </div>

            {/* Social */}
            <div className="flex items-center gap-2">
              {socialLinks?.map(({ id, Icon, href, label }) => (
                <a
                  key={id}
                  href={href}
                  aria-label={label}
                  className="w-8 h-8 rounded-full bg-white/8 flex items-center justify-center text-white/50 hover:text-white hover:bg-[#D93025] transition-all"
                >
                  <Icon />
                </a>
              ))}
            </div>
          </div>

          {/* Discover */}
          <div>
            <h4 className="text-white text-[11px] font-700 uppercase tracking-widest mb-5">Discover</h4>
            <ul className="space-y-2.5">
              {footerLinks?.discover?.map((link) => (
                <li key={link?.id}>
                  <Link href={link?.href} className="text-white/45 text-[13px] hover:text-white transition-colors">
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Categories */}
          <div>
            <h4 className="text-white text-[11px] font-700 uppercase tracking-widest mb-5">Categories</h4>
            <ul className="space-y-2.5">
              {footerLinks?.categories?.map((link) => (
                <li key={link?.id}>
                  <Link href={link?.href} className="text-white/45 text-[13px] hover:text-white transition-colors">
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-white text-[11px] font-700 uppercase tracking-widest mb-5">Company</h4>
            <ul className="space-y-2.5">
              {footerLinks?.company?.map((link) => (
                <li key={link?.id}>
                  <Link href={link?.href} className="text-white/45 text-[13px] hover:text-white transition-colors">
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Newsletter */}
        <div className="mt-10 lg:mt-12 pt-6 lg:pt-8 border-t border-white/8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 lg:gap-5">
            <div>
              <p className="font-700 text-white text-[14px] lg:text-[15px] mb-1" style={{ letterSpacing: '-0.02em' }}>Stay updated with new listings</p>
              <p className="text-white/40 text-[12px] lg:text-[13px]">Get the latest properties delivered to your inbox.</p>
            </div>
            <div className="flex gap-0 w-full sm:w-auto sm:min-w-[340px]">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 bg-white/8 border border-white/12 rounded-l-[10px] text-white placeholder-white/30 px-4 py-3 text-[13px] focus:outline-none focus:border-[#D93025] transition-colors min-w-0"
              />
              <button className="bg-[#D93025] text-white px-4 lg:px-5 py-3 rounded-r-[10px] text-[13px] font-600 hover:bg-[#B71C1C] transition-colors flex items-center gap-1.5 flex-shrink-0 touch-manipulation active:scale-95">
                Subscribe
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Bottom bar */}
      <div className="border-t border-white/6">
        <div className="max-w-screen-xl mx-auto px-4 lg:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-white/30 text-[11px] lg:text-[12px]">
            &copy; 2026 NammaPrice. All rights reserved.
          </p>
          <div className="flex items-center gap-4 lg:gap-5 flex-wrap justify-center">
            {['Privacy Policy', 'Terms of Use', 'Cookie Policy']?.map((item) => (
              <a key={item} href="#" className="text-white/30 text-[11px] lg:text-[12px] hover:text-white/60 transition-colors">
                {item}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}